/**
 * L1J Web Client - Warehouse Dialog
 *
 * NPC 개인 창고 창 (기본 숨김). **꺼내기**(창고 → 인벤토리)와 **넣기**(인벤토리 →
 * 창고)를 한 창의 두 탭으로 처리한다.
 *
 * 목록은 서버에서 한 번만 온다 — `npc_action("retrieve")`의 응답인 `retrieve_list`가
 * 창고에 든 물건 전부를 싣고 오고, 인벤토리는 이미 GameState가 들고 있다. 그래서
 * **탭 전환에는 서버 왕복이 없다.**
 *
 * **창고가 비면 그 목록이 아예 오지 않는다.** 서버 `C_NpcAction`은 `size > 0`일 때만
 * `S_RetrieveList`를 만들고, 아니면 "맡아 둔 짐이 없다"는 HTML(`uish999`)만 보낸다.
 * 목록 도착에만 창을 열면 **빈 창고에는 처음 물건을 넣을 방법이 없어진다** — 넣기 탭이
 * 창 안에 있기 때문이다. 그래서 그 HTML을 받은 HtmlDialog가 `openEmpty()`로 이 창을
 * 빈 목록 상태로 열어 준다 (두 경로 모두 `openPanel()`로 모인다).
 *
 * 거래 확정은 상점과 같은 `C_Result`(shop_result)이고 **`items[].id`가 탭마다 다른
 * 것을 가리킨다** (`types/network.ts`의 ShopResultItem 표):
 *
 *   | 탭 | resultType | items[].id |
 *   |----|-----------|------------|
 *   | 꺼내기 | 3 | **창고** 아이템 objId (`WarehouseEntry.objId`) |
 *   | 넣기   | 2 | **인벤토리** 아이템 objId (`Item.id`) |
 *
 * 두 objId를 섞으면 서버는 조용히 아무 일도 하지 않는다.
 *
 * **가격은 없고 수수료가 있다.** 서버 `C_Result`의 resultType 3 분기는 항목 하나마다
 * `consumeItem(L1ItemId.ADENA, 30)`을 한 번 부른다 — 즉 **꺼내는 개수와 무관한 건당
 * 정액**이고, 그 30이 `S_RetrieveList`가 실어 보내는 `fee`다. 보관(resultType 2)에는
 * 아데나 소모가 없다. 그래서 상점의 "총액" 자리에는 `price * count`가 아니라
 * 탭에 따른 수수료 안내가 들어간다 (`refreshFooter()` 참조).
 *
 * 구조·스타일은 ShopDialog / Inventory와 같은 규약을 따른다:
 *   - Phaser Container + setScrollFactor(0) + UI 카메라 전용
 *   - 자식에게도 setScrollFactor(0)을 직접 걸어야 클릭 히트테스트가 맞는다
 *   - 목록은 가상 스크롤 (행 오브젝트는 고정, 표시 구간만 옮긴다)
 *   - 선택은 줄이 아니라 **항목 id**에 묶는다 (스크롤하면 줄↔항목 대응이 바뀐다)
 *
 * **이 창은 씬 최상위에 붙지 않는다.** GameScene은 UI 전용 카메라를 쓰고 메인
 * 카메라가 무시할 오브젝트를 씬 시작 시 `registerUiObjects()`에서 한 번만 모으므로,
 * 나중에 씬 최상위로 만든 오브젝트는 두 카메라에 이중으로 그려진다. 그래서
 * HtmlDialog(이미 등록돼 있다)의 자식으로 붙어 카메라 배정을 물려받는다.
 */

import Phaser from 'phaser';
import { gameState, WarehouseEntry, WarehouseListState } from '../../core/GameState';
import { Item } from '../../core/types';
import { ConnectionManager } from '../../network/ConnectionManager';
import { resolveItemLabel, resolveItemName, stripCountSuffix } from '../../utils/nameResolver';
import {
  itemIconKind,
  itemIconTextureKey,
  loadItemIcon,
  preloadItemIcons,
} from '../../data/itemIcons';

/** 어느 방향으로 옮기는 중인가. 목록의 출처와 resultType이 여기서 갈린다. */
type WarehouseTab = 'retrieve' | 'deposit';

/** 목록 한 줄의 높이(px). ShopDialog와 같은 값이라 아이콘 규격도 그대로 쓴다. */
const ROW_HEIGHT = 34;

/**
 * 한 화면에 보여줄 줄 수.
 *
 * 상점(8줄)보다 하나 적다 — 제목줄 아래에 탭 줄(26px)이 하나 더 들어가는데
 * 패널 높이는 상점과 같은 440으로 맞췄기 때문이다. 높이를 맞춰 두면
 * HtmlDialog가 "열려 있는 것 중 큰 쪽"을 돌려줄 때 두 창이 같은 영역을 덮는다.
 */
const VISIBLE_ROWS = 7;

/** 줄 배경 (평상시 · 마우스 오버 · 선택). */
const ROW_BG_COLOR = 0x2a2a3a;
const ROW_BG_HOVER_COLOR = 0x3a3a5a;
const ROW_BORDER_COLOR = 0x3a3a5a;
const ROW_BORDER_HOVER_COLOR = 0xffd700;
const ROW_BORDER_SELECTED_COLOR = 0x00ff88;

/** 탭 버튼 색 (선택 / 비선택). */
const TAB_ACTIVE_BG = '#3a3a5a';
const TAB_ACTIVE_COLOR = '#ffd700';
const TAB_IDLE_BG = '#22223a';
const TAB_IDLE_COLOR = '#9a9ac0';

/** 창고 목록 한 줄이 들고 있는 값. 탭에 따라 서로 다른 출처에서 만든다. */
interface WarehouseRowData {
  /** 거래 확정(shop_result)의 `items[].id`. 꺼내기=창고 objId, 넣기=인벤토리 objId. */
  id: number;
  /** 표시명 (이름 ID·상태 라벨 해석 완료, 꼬리 개수 제거). */
  name: string;
  /** 소지/보관 개수. 수량 상한으로도 쓴다. */
  count: number;
  gfxId: number;
  /** `[미감정] [저주] [봉인]` 같은 상태 표시. 평범한 아이템이면 빈 문자열. */
  badge: string;
}

/** 자릿수 구분 — 아데나는 구분 없이는 읽기 어렵다. */
function formatAdena(value: number): string {
  return Math.max(0, Math.floor(value)).toLocaleString('en-US');
}

/**
 * 창고 아이템의 상태 표시를 만든다.
 *
 * 어휘는 `Inventory`의 정보줄(`[미감정]`)에 맞춘다 — 같은 아이템이 창에 따라
 * 다른 말로 불리면 안 된다. 축복/저주는 `blessStatus`(봉인 비트를 뗀 값)가,
 * 봉인은 `sealed`가 근거다 (`GameState.WarehouseEntry` 주석 참조).
 */
function warehouseBadge(entry: WarehouseEntry): string {
  const marks: string[] = [];

  // blessStatus 3은 그 자체가 미감정이다. identified 필드와 어긋날 수 있어 둘 다 본다.
  if (!entry.identified || entry.blessStatus === 3) {
    marks.push('[미감정]');
  } else if (entry.blessStatus === 2) {
    marks.push('[저주]');
  } else if (entry.blessStatus === 0) {
    marks.push('[축복]');
  }

  if (entry.sealed) marks.push('[봉인]');
  return marks.join(' ');
}

/**
 * 인벤토리 원본 표시명을 화면에 쓸 이름으로 바꾼다.
 *
 * 창고 목록의 이름은 GameState가 이미 해석해 주지만(`resolveShopItemName`),
 * `getInventory()`가 돌려주는 이름은 서버 원본(`"$326 ($11)"`)이라 여기서 같은
 * 세 단계를 밟아야 한다 — 꼬리 개수 제거 → 이름 ID 치환 → 상태 라벨 치환.
 */
function resolveInventoryName(rawName: string): string {
  const base = resolveItemName(stripCountSuffix(rawName ?? ''));
  if (!base.includes('($')) return base;
  return base.replace(/\(\$\d+\)/g, (m) => resolveItemLabel(m));
}

export class WarehouseDialog extends Phaser.GameObjects.Container {
  private panelWidth = 560;
  private panelHeight = 440;

  /**
   * 목록 영역 좌측 X / 상단 Y / 폭 / 높이 (모두 컨테이너 로컬 좌표).
   *
   * 상단은 상점보다 26px 아래다 — 제목줄(바닥 -190)과 목록 사이에 탭 줄이 들어간다.
   */
  private listLeft = -this.panelWidth / 2 + 12;
  private listTop = -this.panelHeight / 2 + 66;
  private listWidth = this.panelWidth - 24 - 14; // 오른쪽 14px는 스크롤바 자리
  private listHeight = VISIBLE_ROWS * ROW_HEIGHT;

  private tab: WarehouseTab = 'retrieve';
  /** 창고 NPC. shop_result의 npcObjId로 그대로 나간다. */
  private npcObjId = 0;
  /**
   * 인출 아데나 수수료 (서버가 목록에 실어 보낸 값).
   *
   * **목록이 유일한 출처다.** 빈 창고는 목록 자체가 오지 않으므로 그때는 값을 모른다 —
   * 0으로 두면 "수수료 0"이라는 없는 사실을 화면에 적게 되므로 `null`로 구분한다
   * (`refreshFooter()`). 같은 NPC의 목록이 나중에 오면 그때 실제 값으로 채워진다.
   */
  private fee: number | null = null;

  /** 창고에 든 물건 (꺼내기 탭). 서버 목록을 그대로 옮겨 담는다. */
  private warehouseRows: WarehouseRowData[] = [];
  /** 인벤토리 (넣기 탭). `inventory:update` 때마다 다시 만든다. */
  private inventoryRows: WarehouseRowData[] = [];

  private rows: WarehouseRow[] = [];

  private titleText!: Phaser.GameObjects.Text;
  private countText!: Phaser.GameObjects.Text;
  private emptyText!: Phaser.GameObjects.Text;
  private infoText!: Phaser.GameObjects.Text;
  private qtyText!: Phaser.GameObjects.Text;
  private feeText!: Phaser.GameObjects.Text;
  private confirmText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private tabTexts!: Record<WarehouseTab, Phaser.GameObjects.Text>;

  private scrollTrack!: Phaser.GameObjects.Rectangle;
  private scrollThumb!: Phaser.GameObjects.Rectangle;
  /** 트랙을 누른 채 끄는 중인지. 트랙 밖으로 나가도 이어져야 해서 씬 단위로 추적한다. */
  private isScrubbing = false;

  /** 스크롤 위치(줄 단위). 줄 오브젝트는 VISIBLE_ROWS개로 고정한 가상 스크롤이다. */
  private scrollRow = 0;

  /**
   * 선택은 줄이 아니라 항목에 묶는다 — 스크롤하면 줄↔항목 대응이 바뀌므로
   * 줄에 표시를 남기면 엉뚱한 항목이 선택돼 보인다.
   */
  private selectedId: number | null = null;
  private quantity = 1;

  /** 창이 열리거나 닫힐 때마다 불린다. 부모 컨테이너의 표시 상태를 맞추는 데 쓴다. */
  public onVisibilityChange: (() => void) | null = null;

  /** 사용자가 창을 닫았을 때만 불린다 (`hide(false)`로 닫으면 불리지 않는다). */
  public onDismiss: (() => void) | null = null;

  /**
   * 거래 뒤 이 창이 스스로 `npc_action("retrieve")`를 다시 보낼 때 불린다.
   *
   * 그 응답은 창고에 물건이 남아 있으면 `retrieve_list`지만 **비어 있으면
   * `uish999` HTML**이다. 후자를 창고 신호로 알아보는 쪽은 부모(HtmlDialog)이고,
   * 부모는 자기가 보낸 요청만 기억하므로 여기서 요청 사실을 알려 줘야 한다.
   */
  public onRetrieveRequest: ((npcObjId: number) => void) | null = null;

  /** 씬 input에 직접 건 리스너. destroy 때 해제해야 유령 리스너가 남지 않는다. */
  private sceneInput: Phaser.Input.InputPlugin;
  private onWheel!: (pointer: Phaser.Input.Pointer, objs: unknown, dx: number, dy: number) => void;
  private onPointerMove!: (pointer: Phaser.Input.Pointer) => void;
  private onPointerUp!: () => void;

  /** gameState 구독 해제 함수들. */
  private unsubscribers: Array<() => void> = [];

  /**
   * @param x,y 부모 컨테이너 기준 로컬 좌표.
   *
   * 씬 디스플레이 리스트에 직접 붙이지 않는다 (파일 상단 카메라 주석 참조).
   * 만든 쪽이 `parent.add(warehouseDialog)`로 자식에 넣어야 화면에 나온다.
   */
  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.sceneInput = scene.input;

    this.setScrollFactor(0);

    this.createUI();
    this.registerScrollInput();
    // 구독보다 먼저 닫아 둔다 — 구독 직후 인벤토리를 한 번 읽어 오는데,
    // 그때 창이 "보이는 상태"면 아직 목록을 못 받은 채로 그리려 든다.
    this.setVisible(false);
    this.subscribeWarehouseEvents();

    // 아이콘 23종은 통틀어도 몇 KB라 한 번에 받아둔다. 줄이 그려질 때 텍스처가
    // 이미 있으면 동기 경로로 빠져 깜빡임이 없다 (Inventory와 같은 이유).
    void preloadItemIcons(scene);
  }

  public getPanelWidth(): number {
    return this.panelWidth;
  }

  public getPanelHeight(): number {
    return this.panelHeight;
  }

  /** 창이 열려 있는지. */
  public isOpen(): boolean {
    return this.visible;
  }

  // ==================== 화면 구성 ====================

  private createUI(): void {
    const w = this.panelWidth;
    const h = this.panelHeight;

    const bg = this.scene.add.rectangle(0, 0, w, h, 0x1a1a2e, 0.96);
    bg.setStrokeStyle(2, 0x4a4a6a);
    this.add(bg);

    const titleBar = this.scene.add.rectangle(0, -h / 2 + 15, w, 30, 0x2a2a4e);
    titleBar.setStrokeStyle(1, 0x3a3a5a);
    this.add(titleBar);

    this.titleText = this.scene.add.text(-w / 2 + 12, -h / 2 + 15, '창고', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold',
    });
    this.titleText.setOrigin(0, 0.5);
    this.add(this.titleText);

    // "보이는 범위 / 총 개수". 목록이 꽉 찼을 때 "이게 전부인지"를 알려 준다.
    this.countText = this.scene.add.text(w / 2 - 44, -h / 2 + 15, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#9a9ac0',
    });
    this.countText.setOrigin(1, 0.5);
    this.add(this.countText);

    const closeBtn = this.scene.add.text(w / 2 - 20, -h / 2 + 15, 'X', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ff6666',
    });
    closeBtn.setOrigin(0.5);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    // 걸지 않으면 카메라가 스크롤된 상태에서 히트테스트가 월드 좌표로 계산돼 빗나간다.
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.setScrollFactor(0);
    closeBtn.on('pointerover', () => closeBtn.setStyle({ color: '#ffaaaa' }));
    closeBtn.on('pointerout', () => closeBtn.setStyle({ color: '#ff6666' }));
    // 닫은 뒤 씬이 이 클릭을 월드 클릭으로 오해하지 않도록 알린다
    // (Phaser는 오브젝트 핸들러를 먼저 돌리고 씬 pointerdown을 나중에 쏜다).
    closeBtn.on('pointerdown', () => {
      this.hide();
      this.emit('panelclose');
    });
    this.add(closeBtn);

    this.createTabs();
    this.createRows();
    this.createScrollBar();

    this.emptyText = this.scene.add.text(0, this.listTop + this.listHeight / 2, '', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
    });
    this.emptyText.setOrigin(0.5);
    this.add(this.emptyText);

    this.createFooter();
  }

  /**
   * 탭 줄 — [꺼내기] / [넣기].
   *
   * 두 목록 모두 이미 손에 있으므로(창고 목록은 `retrieve_list`, 인벤토리는
   * GameState) 전환은 서버 왕복 없이 `renderRows()` 한 번으로 끝난다.
   */
  private createTabs(): void {
    const tabY = -this.panelHeight / 2 + 46;
    this.tabTexts = {
      retrieve: this.createTabButton(this.listLeft, tabY, 'retrieve'),
      deposit: this.createTabButton(this.listLeft + 110, tabY, 'deposit'),
    };
  }

  private createTabButton(x: number, y: number, tab: WarehouseTab): Phaser.GameObjects.Text {
    const button = this.scene.add.text(x, y, '', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: TAB_IDLE_COLOR,
      backgroundColor: TAB_IDLE_BG,
      padding: { x: 10, y: 4 },
    });
    button.setOrigin(0, 0.5);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    button.setInteractive({ useHandCursor: true });
    button.setScrollFactor(0);
    button.on('pointerdown', () => this.setTab(tab));
    this.add(button);
    return button;
  }

  /** 줄 오브젝트를 미리 만들어 두고 내용만 갈아 끼운다 (Inventory 슬롯과 같은 방식). */
  private createRows(): void {
    for (let i = 0; i < VISIBLE_ROWS; i++) {
      const row = new WarehouseRow(
        this.scene,
        this.listLeft + this.listWidth / 2,
        this.listTop + ROW_HEIGHT / 2 + i * ROW_HEIGHT,
        this.listWidth,
        ROW_HEIGHT - 2,
        (entry) => this.select(entry)
      );
      this.rows.push(row);
      this.add(row);
    }
  }

  /**
   * 스크롤바. 목록이 한 화면에 다 들어가면 숨는다.
   *
   * 썸은 순수 시각 요소이고 입력은 트랙이 받는다 — 썸은 내용량에 따라 높이가 바뀌는데
   * Phaser에서 setSize()는 이미 등록된 hitArea를 갱신하지 않아 클릭 판정이 어긋난다.
   */
  private createScrollBar(): void {
    const trackX = this.panelWidth / 2 - 14;
    const trackY = this.listTop + this.listHeight / 2;
    const trackWidth = 6;

    this.scrollTrack = this.scene.add.rectangle(trackX, trackY, trackWidth, this.listHeight, 0x0a0a15);
    this.scrollTrack.setStrokeStyle(1, 0x2a2a4a);
    this.scrollTrack.setScrollFactor(0);
    // 6px 트랙은 그대로 두면 집기 어려우므로 판정 영역만 좌우로 넓힌다.
    // hitArea 좌표는 오브젝트 원점이 아니라 좌상단 기준(0,0)~(width,height)이다.
    this.scrollTrack.setInteractive(
      new Phaser.Geom.Rectangle(-(24 - trackWidth) / 2, 0, 24, this.listHeight),
      Phaser.Geom.Rectangle.Contains
    );
    this.scrollTrack.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isScrubbing = true;
      this.scrubToPointer(pointer);
    });
    this.add(this.scrollTrack);

    this.scrollThumb = this.scene.add.rectangle(trackX, trackY, trackWidth, 40, 0x4a4a6a);
    this.scrollThumb.setScrollFactor(0);
    this.add(this.scrollThumb);

    this.updateScrollBar();
  }

  /** 목록 아래 — 선택 정보 · 수량 조절 · 수수료 · 확정/닫기 버튼. */
  private createFooter(): void {
    const w = this.panelWidth;
    const left = -w / 2 + 12;
    const right = w / 2 - 12;
    const infoY = this.listTop + this.listHeight + 20; // 104
    const qtyY = infoY + 34; // 138
    const buttonY = infoY + 74; // 178 (패널 바닥 220까지 42px 여유)

    const divider = this.scene.add.rectangle(0, infoY - 16, w - 24, 1, 0x3a3a5a);
    this.add(divider);

    // 오른쪽 끝에 statusText가 같은 줄을 쓰므로 폭을 나눠 갖는다 (한 줄로 자른다).
    this.infoText = this.scene.add.text(left, infoY, '항목을 선택하세요', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
      wordWrap: { width: w - 240 },
      maxLines: 1,
    });
    this.infoText.setOrigin(0, 0.5);
    this.add(this.infoText);

    const qtyLabel = this.scene.add.text(left, qtyY, '수량', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#9a9ac0',
    });
    qtyLabel.setOrigin(0, 0.5);
    this.add(qtyLabel);

    // -10 / -1 / [값] / +1 / +10. 키보드 입력창을 두지 않는 이유는 채팅 입력과
    // 키 포커스를 다투기 때문이다 (ChatBox가 씬 키보드를 쓴다).
    this.createStepButton(left + 44, qtyY, '-10', -10);
    this.createStepButton(left + 84, qtyY, '-1', -1);

    this.qtyText = this.scene.add.text(left + 124, qtyY, '1', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.qtyText.setOrigin(0.5);
    this.add(this.qtyText);

    this.createStepButton(left + 164, qtyY, '+1', 1);
    this.createStepButton(left + 204, qtyY, '+10', 10);

    // 상점의 "총액" 자리. 창고에는 가격이 없고 인출 수수료만 있다 (파일 상단 주석).
    this.feeText = this.scene.add.text(right, qtyY, '', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      align: 'right',
      wordWrap: { width: 240 },
      maxLines: 1,
    });
    this.feeText.setOrigin(1, 0.5);
    this.add(this.feeText);

    // 거절 사유·연결 상태 안내. 서버가 조용히 무시하는 경우가 많아 한 줄 남긴다.
    this.statusText = this.scene.add.text(right, infoY, '', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffaa66',
      align: 'right',
      wordWrap: { width: 210 },
      maxLines: 1,
    });
    this.statusText.setOrigin(1, 0.5);
    this.add(this.statusText);

    this.confirmText = this.scene.add.text(-70, buttonY, '[ 꺼내기 ]', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.confirmText.setOrigin(0.5);
    this.confirmText.setInteractive({ useHandCursor: true });
    this.confirmText.setScrollFactor(0);
    this.confirmText.on('pointerover', () => this.confirmText.setStyle({ color: '#ffd700' }));
    this.confirmText.on('pointerout', () => this.confirmText.setStyle({ color: '#ffffff' }));
    this.confirmText.on('pointerdown', () => this.confirmTrade());
    this.add(this.confirmText);

    const closeLabel = this.scene.add.text(70, buttonY, '[ 닫기 ]', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    });
    closeLabel.setOrigin(0.5);
    closeLabel.setInteractive({ useHandCursor: true });
    closeLabel.setScrollFactor(0);
    closeLabel.on('pointerover', () => closeLabel.setStyle({ color: '#ffd700' }));
    closeLabel.on('pointerout', () => closeLabel.setStyle({ color: '#ffffff' }));
    closeLabel.on('pointerdown', () => {
      this.hide();
      this.emit('panelclose');
    });
    this.add(closeLabel);
  }

  /** 수량 증감 버튼 하나. */
  private createStepButton(x: number, y: number, label: string, delta: number): void {
    const btn = this.scene.add.text(x, y, label, {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#cccccc',
      backgroundColor: '#2a2a4e',
      padding: { x: 6, y: 3 },
    });
    btn.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.setScrollFactor(0);
    btn.on('pointerover', () => btn.setStyle({ color: '#ffd700' }));
    btn.on('pointerout', () => btn.setStyle({ color: '#cccccc' }));
    btn.on('pointerdown', () => this.changeQuantity(delta));
    this.add(btn);
  }

  // ==================== 입력 (휠 / 스크롤바) ====================

  /**
   * 휠 스크롤과 스크롤바 드래그를 씬 input에 등록한다.
   *
   * 줄 배경이 패널 배경을 덮고 있어 오브젝트 단위 wheel 이벤트로는 줄 위에서 굴린 휠을
   * 놓친다. 그래서 씬 전역에서 받고 포인터가 패널 안인지 직접 판정한다
   * (Inventory / ShopDialog와 동일).
   */
  private registerScrollInput(): void {
    this.onWheel = (pointer, _objs, _dx, dy) => {
      if (!this.visible || !this.isPointerOverPanel(pointer)) return;
      this.setScrollRow(this.scrollRow + (dy > 0 ? 1 : -1));
    };

    this.onPointerMove = (pointer) => {
      if (this.isScrubbing) this.scrubToPointer(pointer);
    };
    this.onPointerUp = () => {
      this.isScrubbing = false;
    };

    this.sceneInput.on('wheel', this.onWheel);
    this.sceneInput.on('pointermove', this.onPointerMove);
    this.sceneInput.on('pointerup', this.onPointerUp);

    // 씬이 재시작돼도 유령 리스너가 남지 않도록 파괴 시 해제한다.
    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.sceneInput.off('wheel', this.onWheel);
      this.sceneInput.off('pointermove', this.onPointerMove);
      this.sceneInput.off('pointerup', this.onPointerUp);
    });
  }

  /**
   * 화면상의 패널 중심. 이 창은 다른 컨테이너의 자식이라 `this.x/y`는 로컬
   * 좌표다 — 포인터(화면 좌표)와 견주려면 부모 변환까지 넣어야 한다.
   * 부모들이 모두 scrollFactor 0이므로 월드 변환이 곧 화면 좌표다.
   */
  private getScreenCenter(): { x: number; y: number } {
    const matrix = this.getWorldTransformMatrix();
    return { x: matrix.tx, y: matrix.ty };
  }

  /** 포인터가 패널 사각형 안에 있는지. */
  private isPointerOverPanel(pointer: Phaser.Input.Pointer): boolean {
    const center = this.getScreenCenter();
    return (
      Math.abs(pointer.x - center.x) <= this.panelWidth / 2 &&
      Math.abs(pointer.y - center.y) <= this.panelHeight / 2
    );
  }

  /** 지금 탭이 보여주는 목록. */
  private getEntries(): WarehouseRowData[] {
    return this.tab === 'retrieve' ? this.warehouseRows : this.inventoryRows;
  }

  /** 스크롤 가능한 최대 줄. 0이면 스크롤할 내용이 없다. */
  private getMaxScrollRow(): number {
    return Math.max(0, this.getEntries().length - VISIBLE_ROWS);
  }

  private setScrollRow(row: number): void {
    const clamped = Phaser.Math.Clamp(row, 0, this.getMaxScrollRow());
    if (clamped === this.scrollRow) return;
    this.scrollRow = clamped;
    this.renderRows();
  }

  /** 트랙에서의 포인터 Y를 스크롤 줄로 환산한다. */
  private scrubToPointer(pointer: Phaser.Input.Pointer): void {
    const maxScroll = this.getMaxScrollRow();
    if (maxScroll === 0) return;

    const localY = pointer.y - this.getScreenCenter().y;
    const ratio = Phaser.Math.Clamp((localY - this.listTop) / this.listHeight, 0, 1);
    this.setScrollRow(Math.round(ratio * maxScroll));
  }

  /** 스크롤 위치·내용량에 맞춰 썸의 크기와 위치를 다시 계산한다. */
  private updateScrollBar(): void {
    const total = this.getEntries().length;
    const maxScroll = this.getMaxScrollRow();
    const needed = maxScroll > 0;
    this.scrollTrack.setVisible(needed);
    this.scrollThumb.setVisible(needed);
    if (!needed) return;

    const thumbHeight = Math.max(20, (this.listHeight * VISIBLE_ROWS) / total);
    const travel = this.listHeight - thumbHeight;

    this.scrollThumb.setSize(this.scrollThumb.width, thumbHeight);
    this.scrollThumb.y = this.listTop + thumbHeight / 2 + (this.scrollRow / maxScroll) * travel;
  }

  // ==================== 목록 구독 ====================

  /**
   * 창고 목록과 인벤토리를 스스로 구독한다 (ShopDialog / CharacterStatsPanel과 같은 방식).
   *
   * `warehouse:close`는 GameState가 목록을 비울 때(맵 이동·로그아웃 등) 온다 —
   * 창만 남고 내용이 사라지는 상태를 막으려면 함께 닫아야 한다.
   *
   * 인벤토리는 창이 닫혀 있어도 계속 받아 둔다. 넣기 탭은 서버에 목록을 청하지
   * 않으므로 GameState가 가진 값이 곧 화면의 근거다.
   */
  private subscribeWarehouseEvents(): void {
    this.unsubscribers.push(
      gameState.onWarehouseList((data) => this.showList(data)),
      gameState.onWarehouseClose(() => this.hide(false)),
      gameState.onInventoryUpdate(({ items }) => this.applyInventory(items))
    );

    this.applyInventory(gameState.getInventory());

    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.unsubscribers.forEach((off) => off());
      this.unsubscribers = [];
    });
  }

  /** 인벤토리를 넣기 탭용 줄 데이터로 바꿔 둔다. */
  private applyInventory(items: Item[]): void {
    this.inventoryRows = items.map((item) => ({
      id: item.id,
      name: this.inventoryLabel(item),
      count: item.count,
      gfxId: item.gfxId,
      // 인벤토리 아이템에는 축복/저주/봉인 정보가 실려 오지 않는다 — 감정 여부만 안다.
      badge: item.identified ? '' : '[미감정]',
    }));

    if (!this.visible || this.tab !== 'deposit') return;

    // 보이는 중이면 곧바로 반영한다. 방금 넣은 아이템이 사라지는 것이 이 경로다.
    this.scrollRow = Phaser.Math.Clamp(this.scrollRow, 0, this.getMaxScrollRow());
    this.renderRows();
    this.refreshFooter();
  }

  /** 인챈트 수치는 인벤토리에만 있으므로 이름 앞에 붙여 둔다 (Inventory 정보줄과 같은 형태). */
  private inventoryLabel(item: Item): string {
    const name = resolveInventoryName(item.name);
    return item.enchantLevel > 0 ? `+${item.enchantLevel} ${name}` : name;
  }

  /**
   * `retrieve_list` 도착 — 창고 목록을 채우고 창을 연다.
   *
   * 같은 NPC의 목록이 다시 온 것이라면(거래 후 재요청) **탭을 유지한다** —
   * 넣기 탭에서 물건을 보내고 나서 꺼내기 탭으로 튕겨 나가면 연속 작업이 끊긴다.
   */
  private showList(data: WarehouseListState): void {
    const rows = (data.items ?? []).map((entry) => ({
      id: entry.objId,
      name: entry.name || `#${entry.objId}`,
      count: entry.count,
      gfxId: entry.gfxId,
      badge: warehouseBadge(entry),
    }));

    // warehouseType 3만 다루지만 서버가 다른 값을 보내면 그대로 드러내 준다.
    const kind = data.warehouseType === 3 ? '개인 창고' : `창고 #${data.warehouseType}`;

    this.openPanel(data.objId, rows, data.fee ?? 0, kind, 'retrieve');
  }

  /**
   * 창고가 비어 있다는 응답(`uish999`)을 받았을 때 — **목록 없이** 창을 연다.
   *
   * 서버는 빈 창고에 `S_RetrieveList`를 보내지 않으므로 이 경로가 없으면 창 자체가
   * 열리지 않고, 그러면 넣기 탭에 닿을 방법이 없어 **처음 물건을 맡길 수가 없다.**
   * 부르는 쪽(HtmlDialog)이 "방금 이 NPC에게 창고를 청했다"를 확인한 뒤에만 부른다.
   *
   * 종류는 `개인 창고`로 고정한다 — 이 클라이언트가 보내는 창고 액션은
   * `"retrieve"`(warehouseType 3) 하나뿐이라 다른 값이 나올 여지가 없다.
   * 첫 탭을 **넣기**로 여는 것도 같은 이유다: 꺼내기 탭은 0개인 것이 이미 확정이고,
   * 이 창이 열린 목적은 넣기 하나뿐이다. (거래 뒤 다시 빈 목록을 받은 경우라면
   * `openPanel()`이 지금 탭을 그대로 둔다.)
   */
  public openEmpty(npcObjId: number): void {
    this.openPanel(npcObjId, [], null, '개인 창고', 'deposit');
  }

  /**
   * 창을 여는 공통 절차 — 목록이 온 경우(`showList`)와 빈 창고(`openEmpty`)가 함께 쓴다.
   *
   * @param fee 목록이 실어 온 수수료. **`null`은 "아직 모른다"**(빈 창고)는 뜻이라
   *   같은 NPC라면 앞서 받아 둔 값을 지우지 않고, 상대가 바뀌면 모름으로 되돌린다.
   * @param tabOnNewNpc 상대가 바뀌었을 때 고를 탭. 같은 NPC면 지금 탭을 유지한다 —
   *   넣기 탭에서 물건을 보내고 나서 꺼내기 탭으로 튕겨 나가면 연속 작업이 끊긴다.
   */
  private openPanel(
    npcObjId: number,
    rows: WarehouseRowData[],
    fee: number | null,
    kind: string,
    tabOnNewNpc: WarehouseTab
  ): void {
    const sameNpc = this.visible && npcObjId === this.npcObjId;

    this.npcObjId = npcObjId;
    this.warehouseRows = rows;
    if (fee !== null) this.fee = fee;
    else if (!sameNpc) this.fee = null;

    if (!sameNpc) this.tab = tabOnNewNpc;
    this.scrollRow = 0;
    this.selectedId = null;
    this.quantity = 1;
    this.statusText.setText('');

    const npcName = gameState.getNpc(npcObjId)?.name;
    this.titleText.setText(npcName ? `${kind} - ${npcName}` : kind);

    // renderRows()가 탭 문구·개수·빈 목록 안내까지 함께 다시 칠한다.
    this.renderRows();
    this.refreshFooter();

    this.setVisible(true);
    this.onVisibilityChange?.();
  }

  /**
   * 창을 닫는다.
   *
   * @param notify false면 `onDismiss`를 부르지 않는다 — 부모가 창 전체를 접는
   *   경로(ESC 등)에서 대화창이 되살아나지 않게 하기 위한 것.
   */
  public hide(notify: boolean = true): void {
    if (!this.visible) return;
    this.setVisible(false);
    this.selectedId = null;

    // 목록의 수명은 창의 수명과 같다 — 닫으면 GameState의 캐시도 비운다
    // (`clearWarehouseList` 주석: 남겨 두면 다음 창고에 이전 목록이 먼저 그려진다).
    // 이때 발행되는 warehouse:close가 다시 여기로 돌아오지만 이미 숨겨져 있어 멈춘다.
    gameState.clearWarehouseList();

    this.onVisibilityChange?.();
    if (notify) this.onDismiss?.();
  }

  // ==================== 탭 · 목록 렌더 ====================

  /** 탭을 바꾼다. 목록이 둘 다 손에 있으므로 서버 왕복이 없다. */
  private setTab(tab: WarehouseTab): void {
    if (this.tab === tab) return;
    this.tab = tab;
    // 선택과 스크롤은 목록마다 따로다 — 넘기면 없는 id를 고른 상태가 된다.
    this.scrollRow = 0;
    this.selectedId = null;
    this.quantity = 1;
    this.statusText.setText('');

    this.renderRows();
    this.refreshFooter();
  }

  /** 탭 버튼의 문구(개수 포함)와 강조 색을 다시 칠한다. */
  private applyTabStyle(): void {
    this.tabTexts.retrieve.setText(`꺼내기 (${this.warehouseRows.length})`);
    this.tabTexts.deposit.setText(`넣기 (${this.inventoryRows.length})`);

    (['retrieve', 'deposit'] as WarehouseTab[]).forEach((tab) => {
      const active = this.tab === tab;
      this.tabTexts[tab].setStyle({
        color: active ? TAB_ACTIVE_COLOR : TAB_IDLE_COLOR,
        backgroundColor: active ? TAB_ACTIVE_BG : TAB_IDLE_BG,
      });
    });

    this.confirmText.setText(this.tab === 'retrieve' ? '[ 꺼내기 ]' : '[ 넣기 ]');
    this.emptyText.setText(
      this.tab === 'retrieve' ? '창고가 비어 있습니다' : '넣을 물건이 없습니다'
    );
  }

  /** 현재 스크롤 위치에 해당하는 구간만 줄에 그린다. */
  private renderRows(): void {
    const entries = this.getEntries();

    this.rows.forEach((row, i) => {
      const entry = entries[this.scrollRow + i];
      if (entry) {
        row.setEntry(entry, entry.id === this.selectedId);
      } else {
        row.clear();
      }
    });

    this.emptyText.setVisible(entries.length === 0);
    this.updateScrollBar();
    this.updateCountText();
    this.applyTabStyle();
  }

  private updateCountText(): void {
    const total = this.getEntries().length;
    if (total === 0) {
      this.countText.setText('0 / 0');
      return;
    }
    const first = this.scrollRow + 1;
    const last = Math.min(total, this.scrollRow + VISIBLE_ROWS);
    this.countText.setText(`${first}-${last} / ${total}`);
  }

  // ==================== 선택 · 수량 · 거래 ====================

  private getSelected(): WarehouseRowData | null {
    if (this.selectedId === null) return null;
    return this.getEntries().find((entry) => entry.id === this.selectedId) ?? null;
  }

  /** 옮길 수 있는 최대 수량. 양쪽 모두 가진 개수를 넘길 수 없다. */
  private getMaxQuantity(entry: WarehouseRowData): number {
    return Math.max(1, entry.count);
  }

  private select(entry: WarehouseRowData): void {
    this.selectedId = entry.id;
    this.quantity = 1;
    this.statusText.setText('');
    this.renderRows();
    this.refreshFooter();
  }

  private changeQuantity(delta: number): void {
    const entry = this.getSelected();
    if (!entry) return;
    this.quantity = Phaser.Math.Clamp(this.quantity + delta, 1, this.getMaxQuantity(entry));
    this.refreshFooter();
  }

  /**
   * 선택 정보 · 수량 · 수수료 표시를 다시 그린다.
   *
   * 수수료는 **꺼내기에만, 그것도 수량과 무관하게 정액으로** 붙는다 — 서버
   * `C_Result`의 resultType 3 분기가 항목 하나마다 `consumeItem(ADENA, 30)`을
   * 한 번 부르고, 이 창은 한 번에 한 항목만 보낸다. 그래서 상점처럼 곱하지 않는다.
   *
   * 액수의 근거는 꺼내기 목록뿐이라 **빈 창고에서는 알 수 없다.** 그때 0을 적으면
   * 없는 사실을 적는 것이고, 지난 창고의 값을 물려 쓰면 남의 값을 적는 것이다.
   * 그래서 모른다고 적는다 — 꺼낼 물건이 0개인 화면이라 실제로 쓸 일도 없고,
   * 그 상태에서 할 수 있는 넣기에는 애초에 수수료가 없다.
   */
  private refreshFooter(): void {
    const entry = this.getSelected();

    const fee = this.fee;
    if (this.tab === 'retrieve') {
      if (fee === null) {
        this.feeText.setText('수수료 미정 (꺼내기 목록을 받으면 표시)');
        this.feeText.setStyle({ color: '#8899bb' });
      } else {
        this.feeText.setText(`수수료 ${formatAdena(fee)} 아데나 (수량 무관)`);
        this.feeText.setStyle({ color: '#ffd700' });
      }
    } else {
      this.feeText.setText('수수료 없음');
      this.feeText.setStyle({ color: '#8899bb' });
    }

    if (!entry) {
      this.infoText.setText('항목을 선택하세요');
      this.infoText.setStyle({ color: '#888888' });
      this.qtyText.setText('-');
      return;
    }

    const badge = entry.badge ? ` ${entry.badge}` : '';
    this.infoText.setText(`선택: ${entry.name}${badge} · 보유 ${entry.count}`);
    this.infoText.setStyle({ color: '#ffffff' });
    this.qtyText.setText(String(this.quantity));
  }

  /**
   * 거래를 확정한다 (C_Result).
   *
   * 서버는 성공/실패 응답을 따로 보내지 않고 **거래 후 목록을 다시 보내주지도
   * 않는다** (`C_Result`의 resultType 2·3 분기 어디에도 `S_RetrieveList`가 없다).
   * 그래서 여기서 `npc_action("retrieve")`를 한 번 더 보내 서버가 아는 목록을
   * 다시 받아 온다 — 지어내지 않는 가장 짧은 방법이다.
   *
   * 다만 `S_RetrieveList`는 **창고가 비면 아예 오지 않으므로**(size > 0일 때만
   * 보낸다), 마지막 물건을 통째로 꺼내는 경우만 화면에서 먼저 지운다. 서버가
   * 거절해 창고에 물건이 남아 있다면 곧 도착하는 목록이 이 낙관적 삭제를 되돌린다.
   * 비어 있을 때 대신 오는 `uish999`도 부모를 거쳐 이 창으로 돌아오므로
   * (`onRetrieveRequest`), 두 응답 어느 쪽이 와도 창은 열린 채로 갱신된다.
   *
   * 거절 사유(무게 초과 270 / 아데나 부족 189 / 거래 불가 210)는 서버 메시지로
   * 채팅창에 뜬다.
   */
  private confirmTrade(): void {
    const entry = this.getSelected();
    if (!entry) {
      this.statusText.setText('먼저 항목을 선택하세요.');
      return;
    }

    const connection = ConnectionManager.getInstance();
    if (!connection.isConnected()) {
      this.statusText.setText('서버와 연결되어 있지 않습니다.');
      return;
    }

    // resultType 2 = 창고에 보관(인벤토리 objId), 3 = 창고에서 인출(창고 objId).
    const retrieving = this.tab === 'retrieve';
    const count = Phaser.Math.Clamp(this.quantity, 1, this.getMaxQuantity(entry));

    connection.getPacketHandler().sendShopResult(this.npcObjId, retrieving ? 3 : 2, [
      { id: entry.id, count },
    ]);

    // 마지막 한 종류를 통째로 꺼내면 새 목록이 오지 않는다 (위 주석).
    if (retrieving && this.warehouseRows.length === 1 && count >= entry.count) {
      this.warehouseRows = [];
    }

    this.selectedId = null;
    this.quantity = 1;
    this.scrollRow = Phaser.Math.Clamp(this.scrollRow, 0, this.getMaxScrollRow());
    this.statusText.setText(retrieving ? '꺼내기를 요청했습니다.' : '보관을 요청했습니다.');
    this.renderRows();
    this.refreshFooter();

    // 갱신된 목록을 다시 청한다. 창은 열어 둔다 — 창고는 여러 개를 연달아 옮긴다.
    // 응답이 `uish999`(빈 창고)로 올 수도 있으므로 요청 사실을 부모에게도 알린다.
    this.onRetrieveRequest?.(this.npcObjId);
    connection.getPacketHandler().sendNpcAction(this.npcObjId, 'retrieve');
  }

  /** Container.setVisible()은 void를 반환한다 — 체이닝을 살려 둔다. */
  public setVisible(visible: boolean): this {
    super.setVisible(visible);
    return this;
  }
}

/**
 * 창고 목록의 한 줄.
 *
 * 아이콘 + 이름(+ 상태 표시) + 개수를 보여주고 클릭하면 선택을 알린다.
 * 스크롤로 내용이 갈리므로 줄 자체는 선택 상태를 갖지 않는다 — 어느 항목이
 * 선택됐는지는 창이 알고, 줄은 그려 달라는 대로 테두리만 바꾼다.
 */
class WarehouseRow extends Phaser.GameObjects.Container {
  private bg!: Phaser.GameObjects.Rectangle;
  private iconImage!: Phaser.GameObjects.Image;
  private iconText!: Phaser.GameObjects.Text;
  private nameText!: Phaser.GameObjects.Text;
  private badgeText!: Phaser.GameObjects.Text;
  private countText!: Phaser.GameObjects.Text;

  private entry: WarehouseRowData | null = null;
  private selected = false;

  /**
   * 아이콘 로드가 비동기라, 로드 중에 스크롤이나 탭 전환으로 내용이 바뀌면 늦게
   * 도착한 텍스처가 엉뚱한 그림을 붙인다. 내용을 갈아 끼울 때마다 증가시켜
   * 뒤늦은 응답을 버린다 (ShopRow / DragGhost와 같은 방식).
   */
  private token = 0;

  private onPick: (entry: WarehouseRowData) => void;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    onPick: (entry: WarehouseRowData) => void
  ) {
    super(scene, x, y);
    this.onPick = onPick;

    this.createUI(width, height);
    scene.add.existing(this);
  }

  private createUI(width: number, height: number): void {
    const iconSize = height - 8;

    this.bg = this.scene.add.rectangle(0, 0, width, height, ROW_BG_COLOR);
    this.bg.setStrokeStyle(1, ROW_BORDER_COLOR);
    this.add(this.bg);

    this.iconImage = this.scene.add.image(-width / 2 + 6 + iconSize / 2, 0, '__DEFAULT');
    this.iconImage.setVisible(false);
    this.add(this.iconImage);

    // 아이콘 텍스처가 아직 없을 때의 폴백. 칸이 비어 보이지 않게 한다.
    this.iconText = this.scene.add.text(-width / 2 + 6 + iconSize / 2, 0, '', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa',
    });
    this.iconText.setOrigin(0.5);
    this.add(this.iconText);

    // 이름이 길어도 오른쪽의 상태·개수를 밀어내지 않도록 한 줄로 자른다.
    const textLeft = -width / 2 + 12 + iconSize;
    this.nameText = this.scene.add.text(textLeft, 0, '', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      wordWrap: { width: width - iconSize - 12 - 190 },
      maxLines: 1,
    });
    this.nameText.setOrigin(0, 0.5);
    this.add(this.nameText);

    // 봉인/저주/미감정은 보조 정보라 개수 왼쪽에 흐리게 붙인다.
    this.badgeText = this.scene.add.text(width / 2 - 70, 0, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#bb99cc',
      wordWrap: { width: 150 },
      maxLines: 1,
    });
    this.badgeText.setOrigin(1, 0.5);
    this.add(this.badgeText);

    this.countText = this.scene.add.text(width / 2 - 8, 0, '', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#8899bb',
    });
    this.countText.setOrigin(1, 0.5);
    this.add(this.countText);

    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    const hitArea = this.scene.add.rectangle(0, 0, width, height, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    hitArea.setScrollFactor(0);
    hitArea.on('pointerover', () => {
      if (!this.entry) return;
      this.bg.setFillStyle(ROW_BG_HOVER_COLOR);
      this.bg.setStrokeStyle(1, ROW_BORDER_HOVER_COLOR);
    });
    hitArea.on('pointerout', () => this.resetStyle());
    hitArea.on('pointerdown', () => {
      if (!this.entry) return;
      this.onPick(this.entry);
    });
    this.add(hitArea);
  }

  public setEntry(entry: WarehouseRowData, selected: boolean): void {
    const token = ++this.token;
    this.entry = entry;
    this.selected = selected;

    this.nameText.setText(entry.name);
    this.badgeText.setText(entry.badge);
    this.countText.setText(`x${entry.count}`);

    this.bg.setVisible(true);
    this.applyIcon(entry, token);
    this.resetStyle();
  }

  /** 빈 줄로 되돌린다. 배경까지 숨겨야 목록이 짧을 때 빈 칸이 눈에 남지 않는다. */
  public clear(): void {
    this.token++;
    this.entry = null;
    this.selected = false;
    this.nameText.setText('');
    this.badgeText.setText('');
    this.countText.setText('');
    this.iconImage.setVisible(false);
    this.iconText.setText('');
    this.bg.setVisible(false);
    this.resetStyle();
  }

  private applyIcon(entry: WarehouseRowData, token: number): void {
    const kind = itemIconKind(entry.gfxId ?? 0, entry.name);
    const key = itemIconTextureKey(kind);

    if (this.scene.textures.exists(key)) {
      this.showTexture(key);
      return;
    }

    this.iconImage.setVisible(false);
    this.iconText.setText('*');
    loadItemIcon(this.scene, kind)
      .then((loadedKey) => {
        if (token !== this.token) return;
        this.showTexture(loadedKey);
      })
      .catch(() => {
        /* 폴백 문자가 이미 떠 있다 */
      });
  }

  private showTexture(key: string): void {
    this.iconImage.setTexture(key);
    // setTexture()는 새 프레임 크기로 width/height를 되돌리므로 매번 다시 잡는다.
    this.iconImage.setDisplaySize(ROW_HEIGHT - 12, ROW_HEIGHT - 12);
    this.iconImage.setVisible(true);
    this.iconText.setText('');
  }

  private resetStyle(): void {
    this.bg.setFillStyle(ROW_BG_COLOR);
    this.bg.setStrokeStyle(1, this.selected ? ROW_BORDER_SELECTED_COLOR : ROW_BORDER_COLOR);
  }
}
